import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import useMutation from "../hooks/useMutation";
import { authLogin, verifyAuthToken } from "../services/authService";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setProfile } from "../redux/slice/profileSlice";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const access_token = useRef(localStorage.getItem("auth_token"));
  const dispatch = useDispatch();

  const [session, setSession] = useState({
    user: null,
    status: "loading",
    error: null,
  });

  const {
    mutate: login,
    error: loginError,
    isPending: loginPending,
    data: loginData,
  } = useMutation({
    mutationFn: async ({ redirect = false, type = "user", ...val }) => {
      const res = await authLogin({ ...val, type });
      return { res, redirect, type, ...val };
    },
    onSuccess: async (payload) => {
      const { res, redirect, type, ...val } = payload;
      if (res?.status === 200) {
        const { accessToken, refreshToken, user } = res;
        localStorage.setItem("auth_token", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        access_token.current = accessToken;

        console.log(res);

        if (redirect) {
          window.location.href = window.location.origin + redirect;
        } else {
          navigate("/");
        }
      } else {
        toast.warning(res?.message);
      }
    },
  });

  async function verifyAuth() {
    const res = await verifyAuthToken();
    if (res?.status === 200) {
      dispatch(
        setProfile({ ...res?.data, access_token: access_token.current })
      );
      setSession((prev) => {
        if (
          prev.user?.access_token === access_token.current &&
          prev.status === "authorized"
        ) {
          return prev;
        }
        return {
          user: { ...res?.data, access_token: access_token.current },
          status: "authorized",
          error: null,
        };
      });
    } else if (res?.data?.invalidToken) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refreshToken");
      setSession((prev) =>
        prev.status === "unauthorized" && prev.error === "Invalid token"
          ? prev
          : { user: null, status: "unauthorized", error: "Invalid token" }
      );
      dispatch(setProfile(null));
      navigate("/");
    } else {
      dispatch(setProfile(null));
      setSession((prev) =>
        prev.status === "unauthorized"
          ? prev
          : { user: null, status: "unauthorized", error: null }
      );
    }
  }

  useEffect(() => {
    verifyAuth();
  }, [location?.key]);

  const authValue = useMemo(
    () => ({
      login,
      loginPending,
      loginData,
      loginError,
      session,
    }),
    [login, loginPending, loginData, loginError, session]
  );

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
